"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CHALLENGE_RESOLUTION_REFUSALS = exports.putRankRewardsSchema = exports.rankBandSchema = exports.PORTAL_REDEEM_REFUSALS = exports.portalRedeemSchema = exports.cancelRedemptionSchema = exports.PRIZE_REDEMPTION_STATUSES = exports.updatePrizeItemSchema = exports.createPrizeItemSchema = exports.updatePrizeCategorySchema = exports.createPrizeCategorySchema = exports.updateChallengeSchema = exports.createChallengeSchema = exports.CHALLENGE_METRICS = exports.CHALLENGE_KINDS = void 0;
const zod_1 = require("zod");
/**
 * Engagement — Challenges/Quests, the Prize Vault and redemptions (SPEC §9,
 * PORTAL-RESEARCH §4–§5).
 *
 * Two audiences share these shapes and the split is the point:
 *
 *  - **Staff** manage the catalogue under `portal.manage` and fulfil or cancel
 *    redemptions. Staff DTOs may carry operational fields (stock, active flags,
 *    who fulfilled what).
 *  - **The portal** shows a customer the vault and their own progress, and lets
 *    them redeem. Portal views carry no other customer's data and nothing
 *    operational — a customer must not learn stock levels beyond "out of
 *    stock", nor who fulfilled someone else's prize.
 *
 * House rules apply throughout: ids in, DTOs out; money never appears here at
 * all (points are integers, not millidinars); dates cross the wire as ISO
 * strings.
 */
/* -------------------------------------------------------------------------- */
/* Challenges & Quests (SPEC §9 Arcade)                                       */
/* -------------------------------------------------------------------------- */
exports.CHALLENGE_KINDS = ['CHALLENGE', 'QUEST'];
/**
 * What a challenge measures. VENUE-OWNED DATA ONLY (PORTAL-RESEARCH §4.5):
 * ggLeap scores challenges from live game telemetry across 17 titles; we do
 * not have that and pretending otherwise would ship challenges that never
 * complete. Every metric here is derivable from tables this venue already
 * writes.
 */
exports.CHALLENGE_METRICS = [
    /** Billed play time, from closed Sessions. Unit: hours. */
    'hours_played',
    /** Money spent, from paid Bills. Unit: thousand IQD. */
    'thousand_iqd_spent',
    /** Distinct business days with at least one sign-in. Unit: days. */
    'visits',
];
/*
 * A CHALLENGE MUST BE COMPLETABLE (owner, 2026-09-13).
 *
 * `metric` and `targetValue` used to be optional — the staff form called the
 * combination a "one-shot". It was not one: nothing accrues against a null
 * metric, and `settleCompletion` only counts a crossing when `targetValue` is
 * a positive number. A challenge saved without either was therefore a row that
 * could never complete and never pay, sitting in the Arcade telling customers
 * to try. That is the fake-UI trap this repo refuses everywhere else, so both
 * are required now.
 *
 * Safe to tighten: production held zero challenges when this landed, so no
 * existing row becomes unsaveable. If manual awards are ever wanted, they want
 * a real "mark complete" action, not a challenge that silently cannot finish.
 */
exports.createChallengeSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.CHALLENGE_KINDS),
    title: zod_1.z.string().trim().min(1, 'Title is required.').max(120),
    description: zod_1.z.string().trim().max(500).optional().nullable(),
    imageMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    /** Omitted or null = venue-wide. No default: an omitted game is not a choice. */
    gameId: zod_1.z.string().min(1).nullable().optional(),
    pointsReward: zod_1.z.number().int().min(0).max(1_000_000),
    targetValue: zod_1.z
        .number()
        .int()
        .min(1, 'A challenge needs a target it can be finished at.')
        .max(1_000_000),
    metric: zod_1.z.enum(exports.CHALLENGE_METRICS, {
        message: 'Pick what this is measured by, or nothing can ever count toward it.',
    }),
    startsAt: zod_1.z.iso.datetime().optional().nullable(),
    endsAt: zod_1.z.iso.datetime().optional().nullable(),
    isActive: zod_1.z.boolean().default(true),
    sortOrder: zod_1.z.number().int().min(0).max(9_999).default(0),
});
/**
 * Every field optional: PATCH semantics, only what changed is written.
 *
 * DECLARED EXPLICITLY, NOT `.partial()`. In zod 4, `.partial()` wraps a
 * `ZodDefault` in `.optional()` but the default still fires, so a PATCH of
 * `{ isActive: false }` came out as `{ isActive: false, sortOrder: 0 }` —
 * every toggle silently reset the field's ordering, and a PATCH that omitted
 * `isActive` re-activated a withdrawn item. Same trap `updateProductSchema`
 * in cafe.ts already avoids the same way. Caught in review.
 */
exports.updateChallengeSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.CHALLENGE_KINDS).optional(),
    title: zod_1.z.string().trim().min(1, 'Title is required.').max(120).optional(),
    description: zod_1.z.string().trim().max(500).optional().nullable(),
    imageMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    // Omitted = leave the game alone; null = make it venue-wide again.
    gameId: zod_1.z.string().min(1).nullable().optional(),
    pointsReward: zod_1.z.number().int().min(0).max(1_000_000).optional(),
    // Nullable is gone here too — a PATCH must not be able to strip a live
    // challenge of the thing that makes it finishable.
    targetValue: zod_1.z.number().int().min(1).max(1_000_000).optional(),
    metric: zod_1.z.enum(exports.CHALLENGE_METRICS).optional(),
    startsAt: zod_1.z.iso.datetime().optional().nullable(),
    endsAt: zod_1.z.iso.datetime().optional().nullable(),
    isActive: zod_1.z.boolean().optional(),
    sortOrder: zod_1.z.number().int().min(0).max(9_999).optional(),
});
exports.createPrizeCategorySchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Name is required.').max(60),
    sortOrder: zod_1.z.number().int().min(0).max(9_999).default(0),
});
/** Explicit for the same `.partial()`-keeps-defaults reason as challenges. */
exports.updatePrizeCategorySchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Name is required.').max(60).optional(),
    sortOrder: zod_1.z.number().int().min(0).max(9_999).optional(),
});
exports.createPrizeItemSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Name is required.').max(120),
    description: zod_1.z.string().trim().max(500).optional().nullable(),
    imageMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    prizeCategoryId: zod_1.z.string().min(1).optional().nullable(),
    pointsCost: zod_1.z.number().int().min(1, 'A prize must cost at least 1 point.').max(10_000_000),
    /** Null/omitted means unlimited. */
    stock: zod_1.z.number().int().min(0).max(1_000_000).optional().nullable(),
    isActive: zod_1.z.boolean().default(true),
    sortOrder: zod_1.z.number().int().min(0).max(9_999).default(0),
});
/** Explicit for the same `.partial()`-keeps-defaults reason as challenges. */
exports.updatePrizeItemSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Name is required.').max(120).optional(),
    description: zod_1.z.string().trim().max(500).optional().nullable(),
    imageMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    prizeCategoryId: zod_1.z.string().min(1).optional().nullable(),
    pointsCost: zod_1.z
        .number()
        .int()
        .min(1, 'A prize must cost at least 1 point.')
        .max(10_000_000)
        .optional(),
    /** Null means unlimited; omitted leaves the stored value alone. */
    stock: zod_1.z.number().int().min(0).max(1_000_000).optional().nullable(),
    isActive: zod_1.z.boolean().optional(),
    sortOrder: zod_1.z.number().int().min(0).max(9_999).optional(),
});
/* -------------------------------------------------------------------------- */
/* Redemptions — staff fulfilment                                             */
/* -------------------------------------------------------------------------- */
exports.PRIZE_REDEMPTION_STATUSES = ['PENDING', 'FULFILLED', 'CANCELLED'];
/**
 * Cancelling restores the points AND the stock in one transaction, and the
 * reason is required: a customer whose redemption vanished with no explanation
 * is a complaint at the counter, and the row is the only record of why.
 */
exports.cancelRedemptionSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1, 'Say why this redemption is being cancelled.').max(200),
});
exports.portalRedeemSchema = zod_1.z.object({
    prizeItemId: zod_1.z.string().trim().min(1).max(64),
});
/**
 * Refusal codes the vault screen must discriminate on. As with login, the
 * client switches on `code`, never on HTTP status.
 */
exports.PORTAL_REDEEM_REFUSALS = [
    'prize_not_found',
    'not_enough_points',
    'out_of_stock',
];
/* -------------------------------------------------------------------------- */
/* Challenge resolution — rank payouts (WAVE2-DESIGN §2)                      */
/* -------------------------------------------------------------------------- */
/** One payout band: ranks rankFrom..rankTo (1-based, inclusive) earn points. */
exports.rankBandSchema = zod_1.z.object({
    rankFrom: zod_1.z.number().int().min(1).max(1_000),
    rankTo: zod_1.z.number().int().min(1).max(1_000),
    points: zod_1.z.number().int().min(1).max(1_000_000),
});
/**
 * PUT /engagement/challenges/:id/rank-rewards — replace-all. Bands must be
 * internally ordered and non-overlapping; gaps are allowed (rank 4 earning
 * nothing between paid bands is a venue's choice, not an error).
 */
exports.putRankRewardsSchema = zod_1.z
    .object({ bands: zod_1.z.array(exports.rankBandSchema).min(1).max(20) })
    .refine((value) => value.bands.every((band) => band.rankFrom <= band.rankTo), {
    message: 'Each band must run from a lower rank to a higher one.',
    path: ['bands'],
})
    .refine((value) => {
    const sorted = [...value.bands].sort((a, b) => a.rankFrom - b.rankFrom);
    return sorted.every((band, index) => index === 0 || band.rankFrom > sorted[index - 1].rankTo);
}, { message: 'Bands must not overlap.', path: ['bands'] });
exports.CHALLENGE_RESOLUTION_REFUSALS = [
    'challenge_not_found',
    'challenge_not_ended',
    'challenge_already_resolved',
];
//# sourceMappingURL=engagement.js.map